import {
  DEFAULT_CURRENCY_CODE,
  type ColumnConfigEntry,
} from '@erp/shared-types';
import {
  formatCurrency,
  formatCurrencyAdvanced,
  parseDateOnlyLocal,
  tryParseMoney,
} from '@erp/shared-utils';

/**
 * How a cell is formatted: a stored column format, or `date_medium` for a
 * `date` column with `dateStyle: 'medium'` (see `columnDisplayFormat`).
 */
export type TaskQueueValueFormat = ColumnConfigEntry['format'] | 'date_medium';

/** The `date_medium` format: "Oct 16, 2026". */
const MEDIUM_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function parseQueueDate(value: Date | string | number): Date | null {
  // A bare YYYY-MM-DD is a calendar day, not UTC midnight: parsing it with
  // new Date() shows the day before in time zones west of UTC.
  const dateOnly = typeof value === 'string' ? parseDateOnlyLocal(value) : null;
  const date = dateOnly ?? (value instanceof Date ? value : new Date(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatQueueDate(
  value: Date | string | number,
  style: 'short' | 'medium' = 'short',
): string | null {
  const date = parseQueueDate(value);
  if (!date) return null;
  return style === 'medium'
    ? MEDIUM_DATE_FORMAT.format(date)
    : date.toLocaleDateString();
}

function formatQueueNumber(
  value: unknown,
  format: 'integer' | 'number' | 'float',
): string | null {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  if (format === 'integer') {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(Math.round(num));
  }
  return new Intl.NumberFormat('en-US').format(num);
}

function formatQueueCurrency(
  value: unknown,
  format: 'currency' | 'currency4',
): string | null {
  const money = tryParseMoney(
    typeof value === 'string' || typeof value === 'number' ? value : null,
  );
  if (!money) return null;
  if (format === 'currency4') {
    return formatCurrencyAdvanced(money.toNumber(), DEFAULT_CURRENCY_CODE, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  }
  return formatCurrency(money.toString());
}

function formatQueueFallback(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) return formatQueueDate(value) ?? '';
  return JSON.stringify(value);
}

/** A date column's cell; anything that isn't a date shows as-is. */
function formatDateCell(value: unknown, style: 'short' | 'medium'): string {
  const date =
    value instanceof Date ||
    typeof value === 'string' ||
    typeof value === 'number'
      ? formatQueueDate(value, style)
      : null;
  return date ?? formatQueueFallback(value);
}

/**
 * Format a custom task-queue column value using the column's `format` hint.
 * Returns an empty string for nullish values so blank cells stay blank.
 */
export function formatConfiguredTaskQueueValue(
  value: unknown,
  format: TaskQueueValueFormat,
): string {
  if (value == null) return '';

  if (format === 'date' || format === 'date_medium') {
    return formatDateCell(value, format === 'date' ? 'short' : 'medium');
  }

  if (format === 'currency' || format === 'currency4') {
    return formatQueueCurrency(value, format) ?? formatQueueFallback(value);
  }

  if (format === 'integer' || format === 'number' || format === 'float') {
    return formatQueueNumber(value, format) ?? formatQueueFallback(value);
  }

  return formatQueueFallback(value);
}

/** Longest compact JSON rendered inline; the cell title carries the rest. */
const COMPACT_VALUE_MAX_CHARS = 80;

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

/**
 * Display text for a task-page path column (`input.*`, `anchor.*`, …).
 * Scalars reuse the column `format`; arrays of scalars join with commas;
 * objects and mixed arrays render as compact JSON, truncated. `title` holds
 * the untruncated text for the cell tooltip when it was shortened.
 */
export function formatTaskPathValue(
  value: unknown,
  format: TaskQueueValueFormat,
): { text: string; title?: string } {
  if (value == null) return { text: '' };
  if (typeof value !== 'object' || value instanceof Date) {
    return { text: formatConfiguredTaskQueueValue(value, format) };
  }
  const full =
    Array.isArray(value) && value.every(isPrimitive)
      ? value.map(String).join(', ')
      : JSON.stringify(value);
  if (full.length <= COMPACT_VALUE_MAX_CHARS) return { text: full };
  return {
    text: `${full.slice(0, COMPACT_VALUE_MAX_CHARS - 1)}…`,
    title: full,
  };
}
